import { axios, cleanAxios } from '~/http-clients'

import type {
  GetTokenResponse,
  ImpersonateResponse,
  RefreshTokenResponse,
  SessionValidationResponse,
} from '../types'

interface LoginRequest {
  userName: string
  password: string
  clientType: string
}

interface RefreshRequest {
  refreshToken: string
  userName: string
}

interface LogoutRequest {
  refreshToken: string
}

interface SsoExchangeRequest {
  code: string
  redirectUri: string
  clientType?: string
}

interface SsoLoginUrlRequest {
  redirectUri: string
  state?: string
}

interface SsoLoginUrlResponse {
  url: string
}

export async function login(request: LoginRequest) {
  const formData = new FormData()
  formData.append('username', request.userName)
  formData.append('password', request.password)
  formData.append('clientType', request.clientType)

  return await cleanAxios.post<GetTokenResponse>(
    '/api/v2/auth/login',
    formData,
    {
      headers: { 'Content-Type': null },
    },
  )
}

export async function refresh({ refreshToken, userName }: RefreshRequest) {
  const formData = new FormData()
  formData.append('refreshToken', refreshToken)
  formData.append('username', userName)
  formData.append('clientType', 'WEB')

  return await cleanAxios.post<RefreshTokenResponse>(
    '/api/v2/auth/refresh',
    formData,
    {
      headers: { 'Content-Type': null },
    },
  )
}

export async function logout({ refreshToken }: LogoutRequest) {
  return await cleanAxios.post<never>('/api/v2/auth/logout', { refreshToken })
}

export async function ssoExchange({
  code,
  redirectUri,
  clientType,
}: SsoExchangeRequest) {
  return await cleanAxios.post<GetTokenResponse>(
    '/api/v2/auth/sso/exchange',
    null,
    { params: { code, redirectUri, clientType } },
  )
}

export async function impersonate({ username }: { username: string }) {
  const formData = new FormData()
  formData.append('username', username)
  return await axios.post<ImpersonateResponse>(
    '/api/v2/auth/impersonate',
    formData,
    { headers: { 'Content-Type': null } },
  )
}

export async function validateSessionToken(token: string) {
  return await cleanAxios.get<SessionValidationResponse>(
    '/api/v2/users/validate',
    { headers: { Authorization: `Bearer ${token}` } },
  )
}

export async function samlCodeExchange({ code }: { code: string }) {
  return await cleanAxios.get<GetTokenResponse>('/api/v2/auth/saml/exchange', {
    params: { code },
  })
}

export async function ssoLoginUrl({ redirectUri, state }: SsoLoginUrlRequest) {
  return await cleanAxios.get<SsoLoginUrlResponse>(
    '/api/v2/auth/sso/login-url',
    {
      params: { redirectUri, state },
    },
  )
}

export async function enableSso(enabled: boolean) {
  return await axios({
    method: 'post',
    url: '/graphql',
    data: JSON.stringify({
      query: `
      mutation UpdateSso($sso: Boolean!) {
          updateSso(sso: $sso) {
            successful
            errors
          }
        }
      `,
      variables: { sso: enabled },
    }),
  })
}
